// frontend/src/widgets/UserListWidget.jsx
// Project:      Vision ANPR by Castle Traffic Ltd - Automatic Number Plate Recognition
// Author:       Leon du Plooy - Castle Traffic Ltd - Copyright 2025
// Contact:      dev@castle-traffic.co.uk
// Created:      2025-06-16
// Updated:      2026-09-07
//
// Description:
//   Widget to fetch and display list of users with roles.
//   Uses shared Axios instance ONLY. Header row is sticky while scrolling.
//
// Mobile responsiveness update (2026-09-07):
//   - Preserves the existing desktop table, sorting, search, API behaviour, and Manage action.
//   - Reflows the SAME table rows into labelled mobile cards below 768px; no duplicate
//     mobile data component is mounted and no additional user API request is introduced.
//   - Adds explicit mobile sort controls because desktop table headers are hidden on mobile.
//   - Measures the first rendered mobile card and sizes the record viewport to exactly one
//     complete record: Name, Email, Role, plus the Manage action row.
//   - Expands the fixed-height mobile page host only as far as required for the mobile
//     controls plus one complete record, replacing desktop-grid-derived mobile clipping.
//   - Uses mandatory mobile scroll snapping so each flick settles on a complete user record.

import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import axiosInstance from "../api/axiosInstance";
import { filterAndSortList, getWidgetTitle } from "../utils/widgetUtils";

const MOBILE_SORT_FIELDS = [
  { value: "first_name", label: "Name" },
  { value: "email", label: "Email" },
  { value: "role", label: "Role" },
];

const UserListWidget = ({ setSelectedUserId }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [searchQuery, setSearchQuery] = useState("");
  const [sortField, setSortField] = useState("first_name");
  const [ascending, setAscending] = useState(true);

  const widgetRootRef = useRef(null);
  const mobileHeaderRef = useRef(null);
  const recordsViewportRef = useRef(null);
  const firstCardRef = useRef(null);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        // NOTE: keep trailing slash to match existing route in this file's history
        const res = await axiosInstance.get("users/");
        if (!cancelled) setUsers(Array.isArray(res.data) ? res.data : []);
      } catch (e) {
        if (!cancelled) {
          setError("Failed to load users");
          setUsers([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSort = (field) => {
    if (field === sortField) {
      setAscending((a) => !a);
    } else {
      setSortField(field);
      setAscending(true);
    }
  };

  const filteredUsers = useMemo(() => {
    return filterAndSortList(
      users,
      searchQuery,
      ["first_name", "last_name", "email", "role"],
      sortField,
      ascending
    );
  }, [users, searchQuery, sortField, ascending]);

  useLayoutEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;

    const root = widgetRootRef.current;
    const header = mobileHeaderRef.current;
    const viewport = recordsViewportRef.current;
    if (!root || !header || !viewport) return undefined;

    const mediaQuery = window.matchMedia("(max-width: 767px)");
    let animationFrame = 0;
    let hostRecord = null;

    const restoreHost = () => {
      if (!hostRecord?.node) return;
      hostRecord.node.style.height = hostRecord.height;
      hostRecord.node.style.minHeight = hostRecord.minHeight;
      hostRecord = null;
    };

    const findFixedMobileHost = () => {
      const rootRect = root.getBoundingClientRect();
      const candidates = [];
      let node = root.parentElement;
      let depth = 0;

      while (node && node !== document.body && depth < 6) {
        const rect = node.getBoundingClientRect();
        const inlineHeight = String(node.style?.height || "").trim();
        const widthMatches = Math.abs(rect.width - rootRect.width) < 90;
        const hasFixedInlineHeight =
          inlineHeight &&
          inlineHeight !== "auto" &&
          inlineHeight !== "100%" &&
          inlineHeight !== "100vh";

        if (widthMatches && hasFixedInlineHeight) {
          candidates.push(node);
        }

        node = node.parentElement;
        depth += 1;
      }

      // The nearest fixed-height ancestor is the page's mobile widget host.
      // WidgetWrapper itself uses percentage/flex sizing and is deliberately skipped.
      return candidates.length ? candidates[0] : null;
    };

    const applyMobileSizing = () => {
      cancelAnimationFrame(animationFrame);
      restoreHost();

      viewport.style.height = "";
      viewport.style.flex = "";
      root.style.height = "";
      root.style.minHeight = "";

      if (!mediaQuery.matches) return;

      animationFrame = requestAnimationFrame(() => {
        const firstCard = firstCardRef.current;
        const cardHeight = firstCard
          ? Math.ceil(firstCard.getBoundingClientRect().height)
          : 220;

        // tbody uses p-3 on mobile: 12px above + 12px below the visible card.
        const recordViewportHeight = cardHeight + 24;
        viewport.style.height = `${recordViewportHeight}px`;
        viewport.style.flex = "0 0 auto";

        // Let the widget root equal mobile controls + exactly one complete user card.
        root.style.height = "auto";
        root.style.minHeight = "0px";

        animationFrame = requestAnimationFrame(() => {
          const host = findFixedMobileHost();
          if (!host) return;

          hostRecord = {
            node: host,
            height: host.style.height,
            minHeight: host.style.minHeight,
          };

          const hostRect = host.getBoundingClientRect();
          const rootRect = root.getBoundingClientRect();
          const wrapperChromeHeight = Math.max(0, rootRect.top - hostRect.top);
          const requiredHostHeight = Math.ceil(
            Math.max(host.scrollHeight, wrapperChromeHeight + rootRect.height + 8)
          );

          host.style.height = `${requiredHostHeight}px`;
          host.style.minHeight = `${requiredHostHeight}px`;
        });
      });
    };

    applyMobileSizing();

    const resizeObserver =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(() => applyMobileSizing())
        : null;

    if (resizeObserver) {
      resizeObserver.observe(header);
      if (firstCardRef.current) resizeObserver.observe(firstCardRef.current);
    }

    const handleMediaChange = () => applyMobileSizing();
    mediaQuery.addEventListener?.("change", handleMediaChange);

    return () => {
      cancelAnimationFrame(animationFrame);
      resizeObserver?.disconnect();
      mediaQuery.removeEventListener?.("change", handleMediaChange);
      restoreHost();
      viewport.style.height = "";
      viewport.style.flex = "";
      root.style.height = "";
      root.style.minHeight = "";
    };
  }, [filteredUsers]);

  const renderSortIndicator = (field) => {
    if (field !== sortField) return null;
    return ascending ? " ▲" : " ▼";
  };

  if (loading) {
    return (
      <p className="text-sm text-gray-500 px-4 py-2 italic">Loading users...</p>
    );
  }

  if (error) {
    return <p className="text-sm text-red-600 px-4 py-2">{error}</p>;
  }

  return (
    <div
      ref={widgetRootRef}
      className="h-auto min-h-0 w-full overflow-hidden rounded-lg border border-gray-200 shadow-sm bg-white flex flex-col md:h-full"
    >
      <div
        ref={mobileHeaderRef}
        className="bg-accent text-white px-3 py-3 md:px-4 md:py-2 flex flex-col gap-3 md:flex-row md:flex-wrap md:justify-between md:items-center"
      >
        <span className="text-sm font-semibold">
          Search the {getWidgetTitle("user_list")}
        </span>

        <input
          type="text"
          placeholder="Search..."
          className="min-h-[44px] w-full rounded border border-white bg-white px-3 py-2 text-sm text-black md:min-h-0 md:w-48 md:px-2 md:py-1"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />

        <div className="grid w-full grid-cols-[minmax(0,1fr)_auto] gap-2 md:hidden">
          <label className="flex min-w-0 flex-col gap-1 text-xs font-semibold">
            Sort by
            <select
              value={sortField}
              onChange={(e) => setSortField(e.target.value)}
              className="min-h-[44px] w-full rounded border border-white bg-white px-3 py-2 text-sm font-normal text-black"
            >
              {MOBILE_SORT_FIELDS.map((field) => (
                <option key={field.value} value={field.value}>
                  {field.label}
                </option>
              ))}
            </select>
          </label>

          <button
            type="button"
            onClick={() => setAscending((current) => !current)}
            className="min-h-[44px] self-end rounded bg-btnGeneralNormal px-4 py-2 text-sm font-semibold text-white shadow hover:bg-btnGeneralHover active:bg-btnGeneralClick"
            aria-label={ascending ? "Sort descending" : "Sort ascending"}
            title={ascending ? "Currently ascending — tap for descending" : "Currently descending — tap for ascending"}
          >
            {ascending ? "▲" : "▼"}
          </button>
        </div>
      </div>

      <div
        ref={recordsViewportRef}
        className="min-h-0 w-full overflow-y-auto overflow-x-hidden overscroll-contain scroll-smooth snap-y snap-mandatory md:flex-1 md:overflow-auto md:snap-none"
      >
        <table className="block w-full text-left text-sm text-gray-700 md:table">
          <thead className="hidden md:table-header-group">
            <tr className="bg-accent text-white cursor-pointer select-none">
              <th
                className="px-4 py-2 sticky top-0 z-10 bg-accent"
                onClick={() => handleSort("first_name")}
                title="Click to sort"
              >
                Name{renderSortIndicator("first_name")}
              </th>
              <th
                className="px-4 py-2 sticky top-0 z-10 bg-accent"
                onClick={() => handleSort("email")}
                title="Click to sort"
              >
                Email{renderSortIndicator("email")}
              </th>
              <th
                className="px-4 py-2 sticky top-0 z-10 bg-accent"
                onClick={() => handleSort("role")}
                title="Click to sort"
              >
                Role{renderSortIndicator("role")}
              </th>
              <th className="px-4 py-2 sticky top-0 z-10 bg-accent">Manage</th>
            </tr>
          </thead>

          <tbody className="block space-y-3 p-3 md:table-row-group md:space-y-0 md:p-0">
            {filteredUsers.length === 0 ? (
              <tr className="block md:table-row">
                <td
                  colSpan="4"
                  className="block rounded-lg border border-gray-200 bg-white px-4 py-5 text-center text-gray-500 italic md:table-cell md:rounded-none md:border-0"
                >
                  No users found.
                </td>
              </tr>
            ) : (
              filteredUsers.map((user, index) => (
                <tr
                  key={user.id}
                  ref={index === 0 ? firstCardRef : undefined}
                  className="block snap-start scroll-mt-3 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm md:table-row md:rounded-none md:border-x-0 md:border-t-0 md:border-b md:border-gray-100 md:shadow-none md:odd:bg-gray-50 md:even:bg-white"
                >
                  <td className="flex items-start justify-between gap-4 border-b border-gray-100 px-4 py-3 md:table-cell md:border-b-0 md:px-4 md:py-2">
                    <span className="shrink-0 text-[11px] font-semibold uppercase tracking-wide text-gray-500 md:hidden">
                      Name
                    </span>
                    <span className="min-w-0 break-words text-right md:text-left">
                      {user.first_name} {user.last_name}
                    </span>
                  </td>

                  <td className="flex items-start justify-between gap-4 border-b border-gray-100 px-4 py-3 md:table-cell md:border-b-0 md:px-4 md:py-2">
                    <span className="shrink-0 text-[11px] font-semibold uppercase tracking-wide text-gray-500 md:hidden">
                      Email
                    </span>
                    <span className="min-w-0 break-all text-right md:text-left">
                      {user.email}
                    </span>
                  </td>

                  <td className="flex items-start justify-between gap-4 border-b border-gray-100 px-4 py-3 md:table-cell md:border-b-0 md:px-4 md:py-2">
                    <span className="shrink-0 text-[11px] font-semibold uppercase tracking-wide text-gray-500 md:hidden">
                      Role
                    </span>
                    <span className="min-w-0 break-words text-right md:text-left">
                      {user.role}
                    </span>
                  </td>

                  <td className="block px-4 py-3 md:table-cell md:px-4 md:py-2">
                    <button
                      type="button"
                      className="min-h-[44px] w-full bg-btnGeneralNormal hover:bg-btnGeneralHover active:bg-btnGeneralClick text-white font-body text-sm px-3 py-2 rounded shadow transition-colors duration-200 ease-in-out md:min-h-0 md:w-auto md:text-xs md:py-1"
                      title="Manage User"
                      onClick={() => setSelectedUserId?.(user.id)}
                    >
                      Manage
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default UserListWidget;
